import { App } from '../App';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DEFAULT_TERM } from '../utils/constants';
import { getAmountAndTermCalculator } from './useApplicationSummary';
import { getClientProductNumber } from '../utils/getProductNumber';
import { Response as GetClientResponse } from '../api/typegen/client/get';
import {
  getDefaultCalculatorFromSession,
  SESSION_CALCULATOR_TYPE,
  setDefaultCalculatorFromSession,
} from '../utils/constantsSession';
import { getFetchHeaders } from '../utils/fetchHeaders';
import { Response } from '../api/typegen/application/first-loan-offer/product-number/{productNumber}/get';
import { useErrorManager } from '../InternalLibrary';
import { useOffer } from './useOffer';
import isLogged, { areClientEndpointsReadyToFetch } from '../utils/isLogged';
import useApplication from './useApplication';
import useClient from './useClient';
import useClientConstraints from './useClientConstraints';

type Props = {
  children: ReactNode;
};

type FetchOptions = {
  pathParameters?: {
    productNumber: string;
  };
  queryParameters: {
    amount: number | string;
    term: number | string;
  };
  client?: GetClientResponse;
};

type ClientOfferContextType = {
  data: Response | undefined | null;
  fetching: boolean;
  fetch: (options?: FetchOptions) => Promise<Response | undefined | null>;
};

const ClientOfferContext = createContext<ClientOfferContextType>(
  {} as ClientOfferContextType,
);

const useClientOffer = (): ClientOfferContextType =>
  useContext(ClientOfferContext);

const ClientOfferProvider = ({ children }: Props): JSX.Element => {
  const [data, setData] = useState<Response | undefined | null>();
  const [fetching, setFetching] = useState(false);
  const { setError } = useErrorManager();
  const { client } = useClient();
  const { data: application, fetch: fetchApplication } = useApplication();
  const { getOfferApi } = useOffer();
  const { data: constraints, fetchClientConstraints } = useClientConstraints();
  const searchParams = new URLSearchParams(window.location.search);

  const paramObj = {
    amount: searchParams.get('amount'),
    term: searchParams.get('term'),
  };

  const isClientLogged = isLogged();

  const sessionCalculatorDataParsed: SESSION_CALCULATOR_TYPE | undefined =
    getDefaultCalculatorFromSession();

  // Save calculator changes from external changes through query params
  useEffect(() => {
    setDefaultCalculatorFromSession(
      Number(paramObj.amount || sessionCalculatorDataParsed?.amount),
      Number(paramObj.term || sessionCalculatorDataParsed?.term),
    );
  }, [paramObj.amount, paramObj.term]);

  const refFetching = useRef(fetching);

  const fetchClientOffer = useCallback(
    async (options?: FetchOptions) => {
      try {
        const amountDefault =
          paramObj.amount ||
          (sessionCalculatorDataParsed?.amount
            ? String(sessionCalculatorDataParsed?.amount)
            : undefined) ||
          data?.newPrincipal ||
          (options?.client || client)?.creditLimit ||
          App.config.fallbackAmount;

        const termDefault =
          paramObj.term ||
          String(
            sessionCalculatorDataParsed?.term || data?.term || DEFAULT_TERM,
          );

        // parameters
        const amount = options?.queryParameters?.amount || amountDefault;
        const term = options?.queryParameters?.term || termDefault;

        setFetching(true);
        refFetching.current = true;

        const apiOffer = getOfferApi({ clientResponse: options?.client });
        const nextData = await apiOffer.fetch({
          headers: getFetchHeaders(),
          pathParameters: {
            productNumber:
              options?.pathParameters?.productNumber ||
              getClientProductNumber(client?.availableProducts),
          },
          queryParameters: {
            amount: Number(amount),
            term: Number(term),
          },
        });
        setData(nextData);
        return nextData;
      } catch (e) {
        setData(null);
        setError();
        throw e;
      } finally {
        setFetching(false);
        refFetching.current = false;
      }
    },
    [setError, sessionCalculatorDataParsed, data, client],
  );

  useEffect(() => {
    if (data !== undefined) setData(undefined);
  }, [isClientLogged]);

  useEffect(() => {
    const fetchData = async () => {
      const clientDataLoaded = areClientEndpointsReadyToFetch(client);
      if (data === undefined && clientDataLoaded) {
        const constraintsData =
          constraints || (isClientLogged && (await fetchClientConstraints()));
        const applicationData =
          application ||
          (isClientLogged ? await fetchApplication() : undefined);
        if (constraintsData) {
          try {
            fetchClientOffer({
              queryParameters: getAmountAndTermCalculator({
                application: applicationData,
                constraints: constraintsData,
              }),
            });
          } catch {
            console.error('Error fetching client offer');
          }
        }
      }
    };
    fetchData();
  }, [constraints, data, isClientLogged, client]);

  const ClientOfferContextValue = useMemo(
    () => ({
      data,
      fetch: fetchClientOffer,
      fetching,
    }),
    [data, fetchClientOffer, fetching],
  );

  return (
    <ClientOfferContext.Provider value={ClientOfferContextValue}>
      {children}
    </ClientOfferContext.Provider>
  );
};

export { useClientOffer as default, ClientOfferProvider };
